/**
 * §14.8 screen 6 — Mahallalar.
 *
 * The full per-mahalla table plus the same bar chart the dashboard truncates to
 * ten rows, and a per-mahalla daily trend when one is selected.
 *
 * **Every mahalla in scope is listed, including the ones with no appeals.**
 * `/admin/stats/mahallas` LEFT JOINs from `mahallas` for exactly this reason
 * (§14.2.2): a mahalla that has filed nothing is a fact worth seeing — it is
 * either a quiet neighbourhood or a kiosk nobody can find — and on a chart that
 * silently dropped it, those two are indistinguishable from a mahalla that does
 * not exist.
 *
 * This screen is `analytics.read`, so a `viewer` reaches it. It therefore shows
 * counts, ratios and dates and NOTHING about a person: no name, no phone, no
 * appeal text, not even a reference.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { ApiError, api, errorKey, jsonBody, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { BarChart } from '../components/charts/index.jsx';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate, formatDateTime, formatMetric, formatPercent } from '../lib/format.js';
import { fromDateInput } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
} from '../ui/index.jsx';

type MahallaStats = components['schemas']['AdminMahallaStatsResponse'];
type Timeseries = components['schemas']['AdminTimeseriesResponse'];

interface Bounds {
  from: string;
  to: string;
}

const EMPTY_BOUNDS: Bounds = { from: '', to: '' };

export function MahallasScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();

  const [bounds, setBounds] = useState<Bounds>(EMPTY_BOUNDS);
  const [applied, setApplied] = useState<Bounds>(EMPTY_BOUNDS);
  const [stats, setStats] = useState<MahallaStats | null>(null);
  const [trend, setTrend] = useState<Timeseries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = { locale };
      const from = fromDateInput(applied.from);
      const to = fromDateInput(applied.to, true);
      if (from) query.from = from;
      if (to) query.to = to;

      const [mahallaStats, timeseries] = await Promise.all([
        api().GET('/admin/stats/mahallas', { params: { query: query as never } }),
        api().GET('/admin/stats/timeseries', {
          params: { query: { bucket: 'week', ...(from ? { from } : {}), ...(to ? { to } : {}) } },
        }),
      ]);
      setStats(jsonBody(unwrap(mahallaStats)));
      setTrend(jsonBody(unwrap(timeseries)));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [applied, locale, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = stats?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <Card
        title={t('admin.mahallas.title')}
        actions={
          <div className="flex items-center gap-2">
            {stats?.scoped ? <Badge tone="gold">{t('admin.scoped.label')}</Badge> : null}
            <Button tone="secondary" onClick={() => setApplied(EMPTY_BOUNDS)}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={() => setApplied(bounds)}>{t('admin.common.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={bounds.from}
              onChange={(event) => setBounds({ ...bounds, from: event.target.value })}
            />
          </Field>
          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={bounds.to}
              onChange={(event) => setBounds({ ...bounds, to: event.target.value })}
            />
          </Field>
          <div className="flex items-end pb-2 text-xs text-ink-500">
            {/* Says which window the table covers — including "everything". */}
            {stats?.from || stats?.to
              ? t('admin.mahallas.window', {
                  from: formatDate(stats.from) ?? '—',
                  to: formatDate(stats.to) ?? '—',
                })
              : t('admin.mahallas.windowAll')}
          </div>
        </div>
      </Card>

      <ErrorBanner message={error} />

      <Card title={t('admin.mahallas.chartTitle')}>
        <BarChart
          label={t('admin.mahallas.chartTitle')}
          items={items.map((item) => ({
            key: item.mahalla_id,
            label: item.mahalla_name,
            value: item.total,
          }))}
          emptyLabel={t('admin.chart.empty')}
        />
      </Card>

      <Card title={t('admin.mahallas.title')}>
        {loading && items.length === 0 ? <Spinner label={t('admin.common.loading')} /> : null}

        {!loading && items.length === 0 ? (
          <EmptyState message={t('admin.mahallas.empty')} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t('admin.column.mahalla')}</Th>
                <Th>{t('admin.column.total')}</Th>
                <Th>{t('shared.murojaat.status.new')}</Th>
                <Th>{t('shared.murojaat.status.in_review')}</Th>
                <Th>{t('shared.murojaat.status.answered')}</Th>
                <Th>{t('shared.murojaat.status.closed')}</Th>
                <Th>{t('admin.column.overdue')}</Th>
                <Th>{t('admin.column.avgDays')}</Th>
                <Th>{t('admin.column.medianDays')}</Th>
                <Th>{t('admin.column.lastReceived')}</Th>
                <Th>{t('admin.column.share')}</Th>
              </tr>
            }
          >
            {items.map((item) => (
              <Tr key={item.mahalla_id}>
                <Td className="font-semibold text-ink-900">{item.mahalla_name}</Td>
                <Td className="tabular-nums">{item.total}</Td>
                <Td className="tabular-nums">{item.new}</Td>
                <Td className="tabular-nums">{item.in_review}</Td>
                <Td className="tabular-nums">{item.answered}</Td>
                <Td className="tabular-nums">{item.closed}</Td>
                <Td className="tabular-nums">
                  {item.overdue > 0 ? (
                    <span className="font-semibold text-danger-500">{item.overdue}</span>
                  ) : (
                    item.overdue
                  )}
                </Td>
                <Td className="tabular-nums">{formatMetric(item.avg_resolution_days)}</Td>
                <Td className="tabular-nums">{formatMetric(item.median_resolution_days)}</Td>
                <Td className="whitespace-nowrap">{formatDateTime(item.last_received_at)}</Td>
                <Td className="tabular-nums">{formatPercent(item.share_pct)}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title={t('admin.mahallas.trendTitle')}>
        {/*
          A weekly count of everything in scope, zero-filled — the companion to
          the table above rather than a second view of it: the table says who,
          this says when.
        */}
        <BarChart
          label={t('admin.mahallas.trendTitle')}
          items={(trend?.items ?? []).map((point) => ({
            key: point.t,
            label: formatDate(point.t),
            value: point.received,
          }))}
          emptyLabel={t('admin.chart.empty')}
        />
      </Card>
    </div>
  );
}
